import { createRouter, createWebHistory } from "vue-router";
import stores from "@/stores";
import { parseJwt, isJwtExpired } from "@/utils";
import { DEFAULT_INBOX_VIEW_FILTERS } from "@/utils/constants.js";
import { DEFAULT_LAYOUT, SETTINGS_LAYOUT } from "@/constants/layouts.js";

import { SquarePen, Users, Tv, Eye, Check } from "lucide-vue-next";

// the route object has the following keys: [ "fullPath", "path", "query", "hash", "name", "params", "matched", "meta", "redirectedFrom", "href" ]
// to get the query params, use "route.query.q"
const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: "/inbox",
      props: (route) => ({ routePath: route.path }),
      meta: {
        requiresAuth: true,
        requiredPermission: "view-inbox-page",
        layout: DEFAULT_LAYOUT,
      },
      children: [
        {
          path: "",
          component: () => import("../views/InboxView.vue"),
        },
        {
          path: ":id",
          component: () => import("../views/InteractionDetailView.vue"),
        },
      ],
    },
    {
      path: "/trainings",
      props: (route) => ({ routePath: route.path }),
      meta: {
        requiresAuth: true,
        requiredPermission: "view-trainings-page",
        layout: DEFAULT_LAYOUT,
      },
      children: [
        {
          path: "",
          component: () => import("../views/TrainingsView.vue"),
        },
      ],
    },
    {
      path: "/recommendations",
      component: () => import("../views/content-gap/ContentGapView.vue"),
      meta: {
        requiresAuth: true,
        requiredPermission: "view-content-recommendations-page",
        layout: DEFAULT_LAYOUT,
      },
    },
    {
      path: "/analytics",
      component: () => import("../views/AnalyticsView.vue"),
      meta: {
        requiresAuth: true,
        requiredPermission: "view-analytics-page",
        layout: DEFAULT_LAYOUT,
      },
    },
    {
      path: "/settings",
      meta: {
        requiresAuth: true,
        requiredPermission: "view-settings-page",
        layout: SETTINGS_LAYOUT,
        inKioskMode: true,
      },
      redirect: () => {
        const user = stores.useUserStore();
        return user.permCodes.includes("view-settings-page.company")
          ? "/settings/company"
          : "/settings/user-profile";
      },
      children: [
        {
          path: "company",
          component: () => import("../views/settings/SettingsCompanyView.vue"),
          meta: {
            requiredPermission: "view-settings-page.company"
          },
        },
        {
          path: "user-profile",
          component: () =>
            import("../views/settings/SettingsUserProfileView.vue"),
          meta: {
            requiredPermission: "view-settings-page.user-profile"
          },
        },
        {
          path: "roles-and-permissions",
          component: () =>
            import("../views/settings/SettingsRolesAndPermissions.vue"),
          meta: {
            requiresAuth: true,
            requiredPermission: "view-settings-page.roles-and-permissions",
          },
        },
        {
          path: "departments",
          component: () =>
            import("../views/settings/SettingsDepartmentsView.vue"),
          meta: {
            requiresAuth: true,
            requiredPermission: "view-settings-page.departments",
          },
        },

        {
          path: "teams",
          component: () => import("../views/settings/SettingsTeamsView.vue"),
          meta: {
            requiresAuth: true,
            requiredPermission: "view-settings-page.teams",
          },
        },

        {
          path: "web_search_topics",
          component: () =>
            import("../views/settings/SettingsWebSearchTopicsView.vue"),
          meta: {
            requiresAuth: true,
            requiredPermission: "view-settings-page.web-search-topics",
          },
        },

        {
          path: "virtual-agent",
          component: () =>
            import("../views/settings/SettingsVirtualAgentView.vue"),
          meta: {
            requiresAuth: true,
            requiredPermission: "view-settings-page.virtual-agent",
          },
        },

        {
          path: "users",
          component: () => import("../views/settings/SettingsUsersView.vue"),
          meta: {
            requiresAuth: true,
            requiredPermission: "view-settings-page.users",
          },
        },

        {
          path: "units",
          component: () => import("../views/settings/SettingsUnitsView.vue"),
          meta: {
            requiresAuth: true,
            requiredPermission: "view-settings-page.units",
          },
        },

        {
          path: "web-forms",
          component: () => import("../views/settings/SettingsWebFormsView.vue"),
          meta: {
            requiresAuth: true,
            requiredPermission: "view-settings-page.web-forms",
          },
        },
        {
          path: "happiness-scores",
          component: () =>
            import("../views/settings/SettingsHappinessScore.vue"),
          meta: {
            requiresAuth: true,
            requiredPermission: "view-settings-page.happiness-scores",
          },
        },
      ],
    },
    {
      path: "/admin",
      meta: {
        requiresAuth: true,
        requiredPermission: "view-admin-page",
      },
      redirect: {
        path: "/admin/dashboard",
      },
      children: [
        {
          path: "dashboard",
          component: () => import("../views/admin/AdminDashboardView.vue"),
        },
        {
          path: "dsat_summary",
          component: () => import("../views/admin/AdminDsatSummaryView.vue"),
        },
        {
          path: "tasks-viewer",
          component: () => import("../views/admin/AdminTaskViewerView.vue"),
        },
      ],
    },
    {
      path: "/announcements",
      meta: { requiresAuth: true, requiredPermission: "view-admin-page" },
      component: () => import("../views/announcements/AnnouncementsView.vue"),
      redirect: { path: "/announcements/create" },
      children: [
        {
          path: "create",
          component: () =>
            import("../views/announcements/CreateAnnouncementsView.vue"),
          redirect: { path: "/announcements/create/draft" },
          children: [
            {
              path: "draft",
              component: () =>
                import("../views/announcements/pages/DraftView.vue"),
              meta: {
                step: 1,
                subRoute: "draft",
                title: "Draft",
                icon: SquarePen,
              },
            },
            {
              path: "users",
              component: () =>
                import("../views/announcements/pages/UsersView.vue"),
              meta: {
                step: 2,
                subRoute: "users",
                title: "Users",
                icon: Users,
              },
            },
            {
              path: "channel",
              component: () =>
                import("../views/announcements/pages/ChannelView.vue"),
              meta: {
                step: 3,
                subRoute: "channel",
                title: "Channel",
                icon: Tv,
              },
            },
            {
              path: "review",
              component: () =>
                import("../views/announcements/pages/ReviewView.vue"),
              meta: {
                step: 4,
                subRoute: "review",
                title: "Review",
                icon: Eye,
              },
            },
            {
              path: "confirmation",
              component: () =>
                import("../views/announcements/pages/ConfirmationView.vue"),
              meta: {
                step: 5,
                subRoute: "confirmation",
                title: "Confirmation",
                icon: Check,
              },
            },
          ],
        },
        {
          path: "list",
          // meta: {
          // },
          children: [
            {
              path: "",
              component: () =>
                import("../views/announcements/pages/CampaignsPage.vue"),
            },
            {
              path: ":id",
              props: true,
              component: () =>
                import("../views/announcements/pages/CampaignPage.vue"),
            },
          ],
        },
      ],
    },
    {
      path: "/messenger",
      component: () => import("@/views/messenger/MessengerView.vue"),
      meta: {
        requiresAuth: true,
        requiredPermission: "view-messenger-page",
        inKioskMode: true,
      },
      redirect: {
        path: "/messenger/chat"
      },
      children: [
        {
          path: "chat",
          component: () => import("@/views/messenger/pages/ChatView.vue"),
        },
        /*
        {
          path: "tickets",
          children: [
            {
              path: "",
              component: () => import("@/views/messenger/pages/InboxView.vue"),
            },
            {
              path: ":id",
              component: () => import("@/views/messenger/pages/TicketView.vue"),
              meta: 
                isTicketPage: true,
              }
            },
          ],
        },
        */
      ],
    },
    {
      path: "/wfb",
      component: () => import("@/views/wfb/WorkflowBuilderView.vue"),
      meta: {
        requiresAuth: true,
        requiredPermission: "view-wfb-chat-page",
        inKioskMode: true,
      },
      redirect: { path: "/wfb/chat" },
      children: [
        {
          path: "chat",
          component: () => import("@/views/wfb/pages/ChatView.vue"),
        },
      ],
    },
    {
      path: "/user-upload",
      meta: {
        requiresAuth: true,
        layout: DEFAULT_LAYOUT,
        requiredPermission: "view-user-upload-page",
      },
      redirect: {
        path: "/user-upload/upload-flow",
      },
      children: [
        {
          path: "upload-flow",
          component: () =>
            import("../views/settings/SettingsUsersUploadView.vue"),
        },
      ],
    },
    {
      path: "/multi-brand-analytics",
      component: () => import("../views/analytics/MultiBrandAnalyticsView.vue"),
      meta: {
        requiresAuth: true,
        requiredPermission: "view-analytics-page",
        layout: DEFAULT_LAYOUT,
      },
    },
    {
      path: "/unit-upload",
      meta: {
        requiresAuth: true,
        layout: DEFAULT_LAYOUT,
        requiredPermission: "view-unit-upload-page",
      },
      redirect: {
        path: "/unit-upload/upload-flow",
      },
      children: [
        {
          path: "upload-flow",
          component: () =>
            import("../views/settings/SettingsUnitsUploadView.vue"),
        },
      ],
    },
    {
      path: "/search",
      component: () => import("../views/SearchView.vue"),
      meta: {
        requiresAuth: true,
        requiredPermission: "view-search-page",
      },
    },
    {
      path: "/auth/login",
      name: "login",
      component: () => import("../views/auth/LoginView.vue"),
      props: (route) => ({ routePath: route.path }),
      meta: {
        requiresAuth: false,
        inKioskMode: true,
      },
    },
    {
      path: "/auth/forgot-password",
      name: "forgot-password",
      component: () => import("../views/auth/ForgotPasswordView.vue"),
      props: (route) => ({ routePath: route.path }),
      meta: {
        requiresAuth: false,
        inKioskMode: true,
      },
    },
    {
      path: "/auth/register",
      name: "register",
      component: () => import("../views/auth/FreeRegisterView.vue"),
      props: (route) => ({ routePath: route.path }),
      meta: {
        requiresAuth: false,
        inKioskMode: true,
      },
    },
    {
      path: "/auth/reset-password",
      name: "reset-password",
      component: () => import("../views/auth/ResetPasswordView.vue"),
      props: (route) => ({ routePath: route.path }),
      beforeEnter: async (to, from, next) => {
        const tokenPayload = parseJwt(to.query.token);
        if (
          !tokenPayload ||
          tokenPayload.type !== "reset_password_link" ||
          isJwtExpired(to.query.token)
        ) {
          to.meta.invalidToken = true;
        }
        return next();
      },
      meta: {
        requiresAuth: false,
        inKioskMode: true,
      },
    },
    {
      path: "/auth/sign-up",
      name: "sign-up",
      component: () => import("../views/auth/SignUpView.vue"),
      props: (route) => ({ routePath: route.path }),
      beforeEnter: async (to, from, next) => {
        const tokenPayload = parseJwt(to.query.token);
        const valid_types = ["self_signup_auth_link", "generic_auth_link"];
        if (
          !tokenPayload ||
          !valid_types.includes(tokenPayload.type) ||
          isJwtExpired(to.query.token)
        ) {
          to.meta.invalidToken = true;
        }
        to.meta.pageSettings = tokenPayload?.page_settings;
        return next();
      },
      meta: {
        requiresAuth: false,
        inKioskMode: true,
      },
    },
    {
      path: "/auth/slack-confirmation",
      name: "slack-confirmation",
      component: () => import("../views/auth/UserConnectionView.vue"),
      props: (route) => ({ routePath: route.path }),
      meta: {
        requiresAuth: false,
        inKioskMode: true,
      },
    },
    {
      path: "/auth/agent-link",
      name: "agent-link",
      component: () => import("../views/auth/AgentLinkView.vue"),
      props: (route) => ({ routePath: route.path }),
      meta: {
        requiresAuth: false,
        inKioskMode: true,
      },
    },
    {
      path: "/maintenance",
      name: "maintenance",
      component: () => import("../views/MaintenanceView.vue"),
      props: (route) => ({ routePath: route.path }),
      meta: {
        requiresAuth: false,
        inKioskMode: true,
      },
    },
    {
      path: "/",
      name: "landing",
      component: () => import("../views/auth/LoginView.vue"),
      props: (route) => ({ routePath: route.path }),
      meta: {
        inKioskMode: true,
      },
    },
    {
      path: "/:pathMatch(.*)*",
      redirect: "/auth/login",
    },
  ],
});


const nextWithKiosk = ({
  to,
  from,
  next,
  desiredNext,
  defaultNext,
}) => {
  const themeStore = stores.useThemeStore();
  if (!themeStore.kioskMode) {
    return next(desiredNext);
  }
  else if (to.meta.inKioskMode === true) {
    if (!desiredNext || to.path === desiredNext.path) {
      return next(desiredNext);
    }

    const desired = router.getRoutes().find(
      (route) => route.path === desiredNext.path
    );
    if (desired.meta.inKioskMode === true) {
      return next(desiredNext);
    }
  }

  return next(defaultNext);
};


/**
 * This function is called before each route change and check if the user is authenticated or not
 */
router.beforeEach(async (to, from, next) => {
  const msgPath = { path: "/messenger" };
  const nextKwargs = {
    to: to,
    from: from,
    next: next,
    defaultNext: msgPath,
  };

  // Special case hacked way of making sure we go to this route
  if (to.path === "/auth/slack-confirmation") {
    console.log("Slack confirmation route. Skip auth checks");
    return nextWithKiosk(nextKwargs);
  }
  else if (to.path === "/auth/agent-link") {
    console.log("Agent Link route. Skip auth checks");
    return nextWithKiosk(nextKwargs);
  }

  console.log(`Changing routes: ${from?.path} => ${to?.path}`);
  const auth = stores.useAuthStore();
  const user = stores.useUserStore();

  console.log("router isLoggedIn");
  const isLoggedIn = await auth.isLoggedIn();

  // if user is logged in, check if users permissions have loaded
  if (isLoggedIn && user.perms.length === 0) {
    console.log("Logged in BUT no permissions. Fetching my profile");
    const profileLoaded = await user.fetchMyProfile();
    // if profile failed to load, we have no permissions
    if (profileLoaded !== true || user.perms.length < 1) {
      // anyone logged in can view messenger
      console.log(
        "Logged in but failed to get Permissions. Going to Messenger"
      );
      return nextWithKiosk({
        ...nextKwargs,
        desiredNext: msgPath,
      });
    }
  }

  if (
    (
      // if there is a pv1_token in the URL, we may actually be logged in
      new URLSearchParams(window.location.search).has("pv1_token") ||
      new URLSearchParams(window.location.search).has("redirect_token")
    ) &&
      !isLoggedIn
  ) {
    await auth.checkAuth();
  }

  // purpose: go to auth routes
  if (!to.meta.requiresAuth && !isLoggedIn) {
    console.log("Not logged in and next doesn't require auth. Going to Next");
    return nextWithKiosk(nextKwargs);
  }

  // purpose: check the status of the user
  if (!isLoggedIn) {
    console.log("Not logged in. Checking auth");
    await auth.checkAuth();
  }

  // this is for if a user goes to a login route and they are already logged in
  if (!to.meta.requiresAuth && isLoggedIn) {
    if (
      user.perms.map((permsObj) => permsObj.code).includes("view-inbox-page")
    ) {
      console.log(
        "Logged in, next page does not require Auth and can view Inbox. Going to Inbox"
      );
      return nextWithKiosk({
        ...nextKwargs,
        desiredNext: { path: "/inbox", query: DEFAULT_INBOX_VIEW_FILTERS },
      });
    } else {
      // everyone on pv2 can view messenger
      console.log(
        "Logged in, next page does not require Auth and CANNOT view Inbox. Going to Messenger"
      );
      return nextWithKiosk({
        ...nextKwargs,
        desiredNext: msgPath,
      });
    }
  }

  // purpose: route does need auth, user is not logged in, redirect to auth
  if (to.meta.requiresAuth && !isLoggedIn) {
    console.log("Requires auth and not logged in. Going to Login");
    return nextWithKiosk({
      ...nextKwargs,
      desiredNext: { path: "/auth/login", query: { redirect: to.fullPath } },
    });
  } // Case: its an auth route and the user is logged in
  else {
    if (
      !to.meta.requiredPermission ||
      user.permCodes.includes(to.meta.requiredPermission)
    ) {
      console.log(
        "No permission required or has required permission. Going to Next"
      );
      return nextWithKiosk(nextKwargs);
    } else {
      // default to messenger page rn
      if (
        user.perms.map((permsObj) => permsObj.code).includes("view-inbox-page")
      ) {
        console.log("Can view Inbox. Going to Inbox");
        return nextWithKiosk({
          ...nextKwargs,
          desiredNext: { path: "/inbox", query: DEFAULT_INBOX_VIEW_FILTERS },
        });
      } else {
        // everyone on pv2 can view messenger
        console.log("Cannot view Inbox. Going to Messenger");
        return nextWithKiosk({
          ...nextKwargs,
          desiredNext: msgPath,
        });
      }
    }
  }
});

export default router;
